import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteNav } from "@/components/site/SiteNav";
import { Footer } from "@/components/site/Sections";
import {
  Maximize2,
  CheckCircle2,
  ArrowRight,
  ChevronRight,
  Sparkles,
  BarChart3,
  TrendingUp,
  Target,
  Users,
  Award,
  Globe,
  Clock,
  ShieldCheck,
  Check,
  Building2,
  FileCheck,
  Layers,
  Search,
  Bus,
  Plane,
  ShoppingBag,
  Zap,
  Eye,
  MapPin,
  Compass,
} from "lucide-react";

export const Route = createFileRoute("/services_/outdoor")({
  head: () => ({
    meta: [
      {
        title:
          "OOH & Outdoor Advertising Services — KMWAdvertising · Billboards, Transit & DOOH",
      },
      {
        name: "description",
        content:
          "Out-of-Home (OOH) Advertising Services That Maximise Visibility, Reach and Brand Recall. Premium Billboards, Transit, Airport, Mall & DOOH Advertising by KMWA LLP.",
      },
      {
        property: "og:title",
        content: "Outdoor (OOH) Advertising Services — KMWAdvertising",
      },
      {
        property: "og:description",
        content:
          "Build Brand Presence Where Your Customers Live, Work and Travel. End-to-end OOH media planning & buying.",
      },
    ],
  }),
  component: OutdoorServicePage,
});

export function OutdoorServicePage() {
  const oohAdvantages = [
    {
      title: "Continuous Brand Visibility",
      desc: "Unlike digital advertisements with limited display time, outdoor media provides continuous visibility 24/7. High-traffic locations ensure your message is seen repeatedly by commuters, pedestrians and motorists.",
      icon: Eye,
    },
    {
      title: "High Reach & Frequency",
      desc: "Strategically selected outdoor locations allow brands to reach large audiences consistently. Frequent exposure strengthens familiarity, increases retention and supports long-term brand building.",
      icon: TrendingUp,
    },
    {
      title: "Unmissable Physical Presence",
      desc: "Billboards, transit media and digital displays occupy physical spaces that consumers naturally encounter during daily routines without requiring a click or device interaction.",
      icon: Maximize2,
    },
    {
      title: "Geographic Precision",
      desc: "OOH allows brands to target specific neighbourhoods, business districts, highways, airports, shopping centres or transit routes for high-impact location-based marketing.",
      icon: MapPin,
    },
    {
      title: "Strengthens Integrated Campaigns",
      desc: "Outdoor advertising works best when combined with TV, radio, print, social media, search and digital ads to multiply brand recognition and overall campaign ROI.",
      icon: Sparkles,
    },
  ];

  const billboardFormats = [
    "Static billboards & bulletin boards",
    "Premium high-impact hoardings",
    "Highway unipoles & arterial displays",
    "Urban center billboards",
    "Backlit & illuminated structures",
    "Wall wraps & building wraps",
    "Large-format outdoor displays",
    "Digital LED billboards",
    "Landmark iconic branding",
  ];

  const transitMediaCategories = [
    {
      title: "Bus Advertising",
      desc: "Full bus wraps, side panels, rear panels, and interior overhead branding across city fleets.",
      icon: Bus,
    },
    {
      title: "Metro Advertising",
      desc: "Station branding, platform screen displays, train exterior wraps, and in-train digital networks.",
      icon: Building2,
    },
    {
      title: "Railway Advertising",
      desc: "Railway station footbridge media, platform hoardings, train exteriors, and concourse displays.",
      icon: Layers,
    },
    {
      title: "Taxi & Cab Branding",
      desc: "Full vehicle wraps, rooftop LED displays, and interior interactive in-cab advertising screens.",
      icon: Compass,
    },
  ];

  const airportMediaFormats = [
    "Digital screen networks",
    "Departure lounge static displays",
    "Arrival hall video walls",
    "Baggage claim belt branding",
    "Escalator & walkway wraps",
    "Security queue lightboxes",
    "Boarding gate archways",
    "Check-in counter screens",
    "Experiential pop-up activations",
  ];

  const mallMediaFormats = [
    "Atrium hanging banners",
    "Escalator & elevator wraps",
    "Food court digital networks",
    "Interactive touch kiosks",
    "Floor graphic decals",
    "Product sampling booths",
    "Experiential event spaces",
  ];

  const doohTechFeatures = [
    "LED high-brightness billboards",
    "Digital street furniture & bus shelters",
    "Transit digital screen networks",
    "Airport digital video walls",
    "Mall interactive digital screens",
    "Smart city connected displays",
    "Programmatic DOOH audience bidding",
  ];

  const siteSelectionMetrics = [
    "Visibility & viewing distance",
    "Traffic count (Vehicular & Pedestrian)",
    "Viewing angle & line-of-sight clearance",
    "Illumination & night-time contrast",
    "Target audience demographic density",
    "Dwell time & exposure duration",
    "Surrounding clutter & environment",
    "Competitive brand presence",
  ];

  const oohBuyingServices = [
    "Billboard space booking & site inspection",
    "Transit media buying & route allocation",
    "Airport terminal ad space buying",
    "Mall network media buying",
    "Digital OOH (DOOH) programmatics",
    "Multi-city campaign management",
    "National outdoor campaign execution",
    "Regional target market buying",
    "Seasonal outdoor promotions",
    "Long-term master branding agreements",
  ];

  return (
    <div className="dark relative min-h-screen bg-background text-foreground flex flex-col w-full overflow-x-clip">
      <SiteNav />

      <main className="flex-1 w-full overflow-x-clip pt-24 md:pt-28">
        {/* HERO SECTION */}
        <section className="relative overflow-hidden py-14 sm:py-20 md:py-24 border-b border-white/10">
          <div className="absolute inset-0 grid-bg opacity-15" aria-hidden />
          <div className="glow-orb -left-32 top-10 h-[450px] w-[450px] bg-[var(--glow-orange)] opacity-25" />
          <div className="glow-orb right-[-100px] top-20 h-[400px] w-[400px] bg-[var(--glow-purple)] opacity-25" />

          <div className="container-x relative z-10">
            {/* Breadcrumb */}
            <div className="flex flex-wrap items-center gap-2.5 text-xs uppercase tracking-[0.2em] text-muted-foreground mb-6">
              <Link to="/" className="hover:text-white transition">
                Home
              </Link>
              <ChevronRight className="h-3 w-3 text-muted-foreground" />
              <Link to="/services" className="hover:text-white transition">
                Services
              </Link>
              <ChevronRight className="h-3 w-3 text-muted-foreground" />
              <span className="text-[var(--glow-orange)] font-semibold">Outdoor (OOH) & Transit Media</span>
            </div>

            <div className="max-w-4xl">
              <div className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5 text-xs uppercase tracking-[0.2em] text-muted-foreground border border-white/10 mb-6">
                <Maximize2 className="h-3.5 w-3.5 text-[var(--glow-orange)]" />
                Traditional Media Capabilities
              </div>

              <h1 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.12] tracking-tight text-white">
                Out-of-Home (OOH) Advertising Services That Maximise Visibility, Reach and Brand Recall
              </h1>

              <p className="mt-6 text-lg sm:text-xl font-accent italic text-[var(--glow-orange)] font-medium border-l-2 border-[var(--glow-orange)] pl-4 py-1">
                Build Brand Presence Where Your Customers Live, Work and Travel
              </p>

              <div className="mt-6 space-y-4 text-base sm:text-lg text-white/90 font-normal leading-relaxed">
                <p>
                  Out-of-Home (OOH) advertising remains one of the most powerful and impactful forms of traditional media. From towering billboards and transit branding to airport displays, mall activations and digital outdoor screens, OOH advertising places your brand directly in front of consumers during their daily journeys.
                </p>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                  Unlike online advertisements that can be skipped or blocked, outdoor advertising becomes part of the environment. It creates repeated exposure, builds familiarity and reinforces brand recognition every time consumers pass the same location. Whether people are commuting, shopping, travelling or attending events, strategically placed OOH campaigns ensure your message stays visible.
                </p>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                  At <strong className="text-white">KMWAdvertising LLP</strong>, we deliver comprehensive OOH advertising solutions that combine strategic media planning, location analysis, creative execution and media buying to maximise campaign performance. With over two decades of experience in integrated advertising, we help brands identify high-impact locations, negotiate premium outdoor inventory and execute campaigns that generate measurable visibility and long-term brand value.
                </p>
              </div>

              <div className="mt-8 flex flex-wrap gap-4">
                <a href="/contact-us" className="btn-hero !py-3.5 !px-7">
                  Request Outdoor Campaign Proposal
                  <ArrowRight className="h-4 w-4" />
                </a>
                <Link to="/services" className="btn-ghost !py-3.5 !px-7">
                  Explore All Services
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* WHY OOH DELIVERS & ADVANTAGES */}
        <section className="py-14 sm:py-20 border-b border-white/10 bg-white/[0.01]">
          <div className="container-x">
            <div className="max-w-3xl mb-12">
              <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-orange)] block mb-2">
                Unmissable Real-World Exposure
              </span>
              <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold text-white">
                Why OOH Advertising Still Delivers Exceptional Results
              </h2>
              <p className="text-sm sm:text-base text-muted-foreground mt-3 leading-relaxed">
                Consumers spend a significant portion of their day outside their homes, commuting to work, shopping, travelling and attending social activities. OOH advertising reaches audiences during these real-world moments, creating repeated brand exposure that complements television, print, radio and digital marketing.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {oohAdvantages.map((adv, idx) => {
                const AIcon = adv.icon;
                return (
                  <div
                    key={idx}
                    className="p-6 sm:p-8 rounded-2xl glass-strong border border-white/10 hover:border-white/20 transition duration-300 flex flex-col justify-between group"
                  >
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-orange)]">
                          ADVANTAGE 0{idx + 1}
                        </span>
                        <div className="h-10 w-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-white">
                          <AIcon className="h-5 w-5" />
                        </div>
                      </div>
                      <h3 className="font-display text-xl font-bold text-white mb-2">
                        {adv.title}
                      </h3>
                      <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                        {adv.desc}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* OUR OOH ADVERTISING SERVICES */}
        <section className="py-16 md:py-24 border-b border-white/10 relative">
          <div className="container-x">
            <div className="max-w-3xl mb-12">
              <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-orange)] block mb-2">
                End-to-End Outdoor Formats
              </span>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">
                Our OOH Advertising Services
              </h2>
              <p className="text-sm sm:text-base text-muted-foreground mt-2">
                At KMWAdvertising LLP, we offer end-to-end outdoor advertising solutions tailored to your business objectives, target audience and budget.
              </p>
            </div>

            {/* BILLBOARD ADVERTISING */}
            <div className="rounded-3xl glass-strong border border-white/15 p-6 sm:p-10 md:p-12 mb-12 relative overflow-hidden shadow-2xl">
              <div className="glow-orb -top-32 -right-32 h-96 w-96 rounded-full bg-[var(--glow-orange)] opacity-15" />

              <div className="grid lg:grid-cols-12 gap-8 items-start relative z-10">
                <div className="lg:col-span-5 space-y-4">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-mono text-[var(--glow-orange)] bg-[var(--glow-orange)]/10 border border-[var(--glow-orange)]/30">
                    <Maximize2 className="h-3.5 w-3.5" />
                    High-Impact Landmarks
                  </div>
                  <h3 className="font-display text-2xl sm:text-3xl font-bold text-white">
                    Billboard Advertising
                  </h3>
                  <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider">
                    High-Impact Brand Visibility Across Premium Locations
                  </p>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                    Billboards remain one of the most recognised forms of outdoor advertising. Their large format and strategic placement make them ideal for building awareness, promoting products and strengthening brand presence across arterial highways and urban centers.
                  </p>
                </div>

                <div className="lg:col-span-7 bg-[#06060c]/90 rounded-2xl p-6 border border-white/10">
                  <h4 className="text-xs font-mono uppercase tracking-widest text-white/80 mb-4 block">
                    Our Billboard Options Include:
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {billboardFormats.map((format, fIdx) => (
                      <div
                        key={fIdx}
                        className="flex items-center gap-2.5 p-2.5 rounded-xl bg-white/[0.03] border border-white/5 text-xs text-white/90"
                      >
                        <Check className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
                        <span>{format}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* TRANSIT ADVERTISING */}
            <div className="mb-12">
              <div className="border-b border-white/10 pb-4 mb-6">
                <h3 className="font-display text-2xl font-bold text-white flex items-center gap-2.5">
                  <Bus className="h-5 w-5 text-[var(--glow-orange)]" />
                  Transit Advertising
                </h3>
                <p className="text-xs sm:text-sm font-mono text-muted-foreground uppercase tracking-wider mt-1">
                  Reach Consumers Throughout Their Daily Commute
                </p>
              </div>

              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {transitMediaCategories.map((tm, idx) => {
                  const TIcon = tm.icon;
                  return (
                    <div
                      key={idx}
                      className="p-6 rounded-2xl glass-strong border border-white/10 hover:border-white/20 transition duration-300 space-y-3"
                    >
                      <div className="h-10 w-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-white">
                        <TIcon className="h-5 w-5 text-[var(--glow-orange)]" />
                      </div>
                      <h4 className="font-display text-lg font-bold text-white">{tm.title}</h4>
                      <p className="text-xs text-muted-foreground leading-relaxed">{tm.desc}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* AIRPORT, MALL & DOOH GRID */}
            <div className="grid lg:grid-cols-3 gap-6 mb-16">
              {/* AIRPORT */}
              <div className="p-6 rounded-2xl glass-strong border border-white/10 space-y-4">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-mono text-sky-400 bg-sky-500/10 border border-sky-500/30">
                  <Plane className="h-3.5 w-3.5" />
                  High-Value Travellers
                </div>
                <h4 className="font-display text-xl font-bold text-white">Airport Advertising</h4>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Connect with business professionals and leisure travellers with high dwell time:
                </p>
                <div className="space-y-1.5">
                  {airportMediaFormats.map((af, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-white/90">
                      <Check className="h-3 w-3 text-sky-400 shrink-0" />
                      <span>{af}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* MALL */}
              <div className="p-6 rounded-2xl glass-strong border border-white/10 space-y-4">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-mono text-pink-400 bg-pink-500/10 border border-pink-500/30">
                  <ShoppingBag className="h-3.5 w-3.5" />
                  Point of Purchase
                </div>
                <h4 className="font-display text-xl font-bold text-white">Mall Advertising</h4>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Influence shopper decisions during high purchase-intent visits:
                </p>
                <div className="space-y-1.5">
                  {mallMediaFormats.map((mf, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-white/90">
                      <Check className="h-3 w-3 text-pink-400 shrink-0" />
                      <span>{mf}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* DOOH */}
              <div className="p-6 rounded-2xl glass-strong border border-white/10 space-y-4">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-mono text-emerald-400 bg-emerald-500/10 border border-emerald-500/30">
                  <Zap className="h-3.5 w-3.5" />
                  Tech-Enabled Screens
                </div>
                <h4 className="font-display text-xl font-bold text-white">Digital OOH (DOOH)</h4>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Dynamic screens powered by real-time scheduling and programmatic bidding:
                </p>
                <div className="space-y-1.5">
                  {doohTechFeatures.map((df, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-white/90">
                      <Check className="h-3 w-3 text-emerald-400 shrink-0" />
                      <span>{df}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* OOH MEDIA PLANNING & BUYING */}
            <div className="grid lg:grid-cols-2 gap-8">
              {/* OOH PLANNING */}
              <div className="p-8 sm:p-10 rounded-3xl glass-strong border border-white/15 space-y-6">
                <div>
                  <h3 className="font-display text-2xl font-bold text-white flex items-center gap-2">
                    <BarChart3 className="h-5 w-5 text-[var(--glow-orange)]" />
                    OOH Media Planning
                  </h3>
                  <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider mt-1">
                    Data-Driven Site & Location Selection
                  </p>
                </div>
                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  Successful outdoor advertising begins with selecting the right locations. Every site is assessed based on visibility, traffic count, viewing angle, illumination, and demographic density.
                </p>

                <div>
                  <h5 className="text-xs font-mono uppercase tracking-widest text-white/80 mb-3 block">
                    Site Selection Evaluation Metrics:
                  </h5>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {siteSelectionMetrics.map((sm, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs text-white/90">
                        <Check className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
                        <span>{sm}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* OOH BUYING */}
              <div className="p-8 sm:p-10 rounded-3xl glass-strong border border-white/15 space-y-6 bg-[#06060c]/90">
                <div>
                  <h3 className="font-display text-2xl font-bold text-white flex items-center gap-2">
                    <ShieldCheck className="h-5 w-5 text-[var(--glow-orange)]" />
                    OOH Media Buying
                  </h3>
                  <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider mt-1">
                    Secure Premium Outdoor Inventory at Competitive Rates
                  </p>
                </div>
                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  Outdoor media buying requires extensive market knowledge and supplier relationships. Our team works directly with media owners to secure premium inventory while optimising campaign budgets.
                </p>

                <div>
                  <h5 className="text-xs font-mono uppercase tracking-widest text-white/80 mb-3 block">
                    Our OOH Buying Scope Includes:
                  </h5>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {oohBuyingServices.map((obs, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs text-white/90">
                        <Check className="h-3.5 w-3.5 text-[var(--glow-orange)] shrink-0" />
                        <span>{obs}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CLOSING CTA BANNER */}
        <section className="py-20 md:py-28 relative overflow-hidden">
          <div className="glow-orb left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[500px] w-[500px] bg-[var(--glow-orange)] opacity-20" />

          <div className="container-x relative z-10 text-center max-w-4xl">
            <div className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5 text-xs uppercase tracking-widest text-muted-foreground border border-white/10 mb-6">
              <Maximize2 className="h-3.5 w-3.5 text-[var(--glow-orange)]" />
              Outdoor Media Advisory
            </div>

            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-white leading-tight">
              Ready to Scale Your Brand Presence with Outdoor Advertising?
            </h2>

            <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              Connect with KMWAdvertising LLP's outdoor media buying and planning specialists to build high-impact OOH campaigns tailored to your target locations.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href="/contact-us" className="btn-hero w-full sm:w-auto">
                Request OOH Proposal
                <ArrowRight className="h-4 w-4" />
              </a>
              <Link to="/services" className="btn-ghost w-full sm:w-auto">
                Back to All Services
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
